import React from 'react';
import styles from './CadArrestsInformation.module.scss';
import {
  CadAddressValueType,
  CadArrestChargeValueType,
  CadArrestValueType,
  CadOffenseValueType,
  CadPersonValueType,
  CadPremiseValueType,
  CadSubjectValueType
} from '../../../../../Models/CadReportValue';
import {
  FieldItemList,
  FieldItemListType
} from '../../CommonBody/FieldItemList/FieldItemList';
import {
  getCADAddressFull,
  getCADPersonnelFullName,
  getCADPremiseName,
  getCADSubjectFullName
} from '../../CommonBody/Helpers';

const getChargeFields = (
  charge: CadArrestChargeValueType,
  offenses: CadOffenseValueType[],
  personnel: CadPersonValueType[]
): FieldItemListType[] => {
  const offense = offenses.find((offense) => offense.id === charge.offense_id);

  return [
    {
      label: 'ID',
      value: offense?.id || charge.offense_id,
      type: 'string'
    },
    {
      label: 'Code',
      value: offense?.code || '',
      type: 'string'
    },
    {
      label: 'Code Description',
      value: offense?.code_description || '',
      type: 'string'
    },
    {
      label: 'IBR Code',
      value: offense?.ibr_code || '',
      type: 'string'
    },
    {
      label: 'UCR Code',
      value: offense?.ucr_code || '',
      type: 'string'
    },
    {
      label: 'Officer Assigned to the Case',
      value: getCADPersonnelFullName(
        offense?.officer_assigned_badge_id as string,
        personnel as CadPersonValueType[]
      ),
      type: 'string'
    },
    {
      label: 'Status',
      value: offense?.status || '',
      type: 'string'
    },
    {
      label: 'Type',
      value: charge.type,
      type: 'string'
    }
  ];
};

const getArrestFields = (
  arrest: CadArrestValueType,
  premises: CadPremiseValueType[],
  adddresses: CadAddressValueType[],
  personnel: CadPersonValueType[],
  subjects: CadSubjectValueType[],
  offenses: CadOffenseValueType[]
): FieldItemListType[] => {
  let arrestFields: FieldItemListType[] = [
    {
      label: 'Case Number',
      value: arrest.case_number,
      type: 'string'
    },
    {
      label: 'Agency',
      value: arrest.agency,
      type: 'string'
    },
    {
      label: 'Subject',
      value: getCADSubjectFullName(arrest.subject_id, subjects),
      type: 'string'
    },
    {
      label: 'Type',
      value: arrest.type,
      type: 'string'
    },
    {
      label: 'Date and Time',
      value: arrest.date_time,
      type: 'datetime'
    }
  ];

  if (arrest.premise_id) {
    arrestFields.push({
      label: 'Premise',
      value: getCADPremiseName(arrest.premise_id, premises),
      type: 'string'
    });
  }

  if (arrest.address_id) {
    arrestFields.push({
      label: 'Address',
      value: getCADAddressFull(
        arrest.address_id,
        adddresses as CadAddressValueType[]
      ),
      type: 'string'
    });
  }

  if (arrest.officer_badge_id) {
    arrestFields.push({
      label: 'Officer',
      value: getCADPersonnelFullName(
        arrest.officer_badge_id,
        personnel as CadPersonValueType[]
      ),
      type: 'string'
    });
  }

  if (arrest.charges && arrest.charges.length > 0) {
    arrestFields.push({
      label: 'Charges',
      value: arrest.charges.map((charge) => {
        return getChargeFields(charge, offenses, personnel);
      }),
      type: 'subList'
    });
  }

  return arrestFields;
};

export interface CadArrestsInformationProps {
  arrests?: CadArrestValueType[];
  premises?: CadPremiseValueType[];
  addresses?: CadAddressValueType[];
  personnel?: CadPersonValueType[];
  subjects?: CadSubjectValueType[];
  offenses?: CadOffenseValueType[];
}

export const CadArrestsInformation = ({
  arrests,
  premises,
  addresses,
  personnel,
  subjects,
  offenses
}: CadArrestsInformationProps): React.ReactElement<CadArrestsInformationProps> => {
  return (
    <div className={styles.cadArrestsInformation}>
      <h3 className={styles.title}>Arrest Information</h3>
      <div className={styles.items}>
        {arrests?.map((arrest, index) => (
          <FieldItemList
            key={arrest.id}
            title={`${index === 0 ? 'Nº ' + arrest.number : ''}`}
            fields={getArrestFields(
              arrest as CadArrestValueType,
              premises as CadPremiseValueType[],
              addresses as CadAddressValueType[],
              personnel as CadPersonValueType[],
              subjects as CadSubjectValueType[],
              offenses as CadOffenseValueType[]
            )}
            hasDivider={index < arrests.length - 1}
          />
        ))}
      </div>
    </div>
  );
};
